import { useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeSync } from 'normalize-diacritics';

import firebaseHooks from '../../firebase/hooks';
import { CompanyType, ModificationMode, UserPermission } from '../../enums';
import { useForm, useNewCompany } from '../../hooks';
import { RequirePermission } from '../../security';
import { CompanyForm, FormControlHeader } from '../../components';
import getCompanySchema from '../../validation/CompanyValidations';

const AddCompany: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const company = useNewCompany(CompanyType.CorporateCompany);
  const mode = ModificationMode.Add;
  const firestore = firebaseHooks.useFirestore();

  const companyValidations = useMemo(
    () => getCompanySchema(firestore, CompanyType.CorporateCompany),
    [firestore]
  );

  const pathPrefix = '/admin/corporate-companies';

  const form = useForm(company, {
    mode: mode,
    validations: companyValidations,
    onRevert: () => {
      navigate(pathPrefix);
      return false;
    },
    beforeSave: (item) => {
      item.nameNormalized = normalizeSync(item.name ?? '')
        .toLowerCase()
        .replace(/ /g, '');
    },
    onSaveSuccess: () => {
      navigate(pathPrefix);
    },
  });

  return (
    <RequirePermission oneOf={[UserPermission.ManageCorporateCompanies]}>
      <FormControlHeader
        backTo={pathPrefix}
        form={form}
        title={t('companies.form.title', { name: company.item?.name })}
        saveButtonLabel={t('companies.actions.add')}
      />
      <CompanyForm form={form} mode={mode} />
      <Outlet context={form} />
    </RequirePermission>
  );
};

export default AddCompany;
